import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  me: () => api.get('/auth/me'),
};

// Space Types API
export const spaceTypesAPI = {
  getAll: () => api.get('/space-types'),
  create: (spaceType) => api.post('/space-types', spaceType),
  update: (id, spaceType) => api.put(`/space-types/${id}`, spaceType),
  importProducts: (products) => api.post('/products/import', { products }),
};

// Stores API
export const storesAPI = {
  getAll: () => api.get('/stores'),
  create: (store) => api.post('/stores', store),
  update: (id, store) => api.put(`/stores/${id}`, store),
  importStores: (stores) => api.post('/stores/import', { stores }),
};

// Store Dependents API
export const dependentsAPI = {
  getAll: (storeId) => api.get('/store-dependents', { params: { store_id: storeId } }),
  getByStore: (storeId) => api.get(`/stores/${storeId}/dependents`),
  create: (storeId, dependent) => api.post(`/stores/${storeId}/dependents`, dependent),
  update: (storeId, dependentId, dependent) => api.put(`/stores/${storeId}/dependents/${dependentId}`, dependent),
  delete: (storeId, dependentId) => api.delete(`/stores/${storeId}/dependents/${dependentId}`),
  getStoresWithCount: () => api.get('/stores-with-dependents-count'),
  sendStoreSummary: (storeId) => api.post(`/stores/${storeId}/send-monthly-summary`),
  sendAllSummaries: () => api.post('/send-all-monthly-summaries'),
};

// Capacities API
export const capacitiesAPI = {
  getAll: (storeId) => api.get('/capacities', { params: { store_id: storeId } }),
  create: (capacity) => api.post('/capacities', capacity),
  update: (id, capacity) => api.put(`/capacities/${id}`, capacity),
};

// Requests API
export const requestsAPI = {
  getAll: (status, providerId) => api.get('/requests', { params: { status, provider_company_id: providerId } }),
  getById: (id) => api.get(`/requests/${id}`),
  create: (request) => api.post('/requests', request),
  addLineItem: (requestId, lineItem) => api.post(`/requests/${requestId}/line-items`, lineItem),
  addAllocation: (requestId, allocation) => api.post(`/requests/${requestId}/allocations`, allocation),
  submit: (requestId) => api.post(`/requests/${requestId}/submit`),
  approve: (requestId) => api.post(`/requests/${requestId}/approve`),
  approveReject: (requestId, data) => api.post(`/requests/${requestId}/approve-reject`, data),
  updateStatus: (requestId, data) => api.put(`/requests/${requestId}/status`, data),
  addComment: (requestId, comment) => api.post(`/requests/${requestId}/comments`, comment),
  getComments: (requestId) => api.get(`/requests/${requestId}/comments`),
  exportReport: (requestId) => api.get(`/requests/${requestId}/export-report`, { responseType: 'blob' }),
};

// Artworks API
export const artworksAPI = {
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/artworks/upload-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  create: (requestId, spaceTypeId, fileUrls) => {
    const formData = new FormData();
    formData.append('request_id', requestId);
    formData.append('space_type_id', spaceTypeId);
    formData.append('file_urls', JSON.stringify(fileUrls));
    return api.post('/artworks', formData);
  },
  getByRequest: (requestId) => api.get(`/artworks/${requestId}`),
  review: (artworkId, data) => api.put(`/artworks/${artworkId}/review`, data),
};

// Inventory API
export const inventoryAPI = {
  checkAvailability: (params) => api.get('/inventory/availability', { params }),
  getSummary: (month, storeId, spaceTypeId) => api.get('/inventory/summary', { 
    params: { month, store_id: storeId, space_type_id: spaceTypeId } 
  }),
  getCalendar: (storeId, spaceTypeId, startMonth, endMonth) => api.get('/inventory/calendar', {
    params: { store_id: storeId, space_type_id: spaceTypeId, start_month: startMonth, end_month: endMonth }
  }),
  checkAvailabilityRange: (storeId, spaceTypeId, startMonth, endMonth, quantity = 1) => 
    api.post('/inventory/check-range', null, {
      params: { store_id: storeId, space_type_id: spaceTypeId, start_month: startMonth, end_month: endMonth, quantity }
    }),
};

// Availability Blocks API (Admin - Airbnb style)
export const blocksAPI = {
  getAll: (storeId, spaceTypeId, activeOnly = true) => api.get('/availability-blocks', {
    params: { store_id: storeId, space_type_id: spaceTypeId, active_only: activeOnly }
  }),
  create: (data) => api.post('/availability-blocks', data),
  delete: (blockId) => api.delete(`/availability-blocks/${blockId}`),
  getCalendar: (storeId, spaceTypeId, startMonth, endMonth) => 
    api.get(`/availability-blocks/calendar/${storeId}/${spaceTypeId}`, {
      params: { start_month: startMonth, end_month: endMonth }
    }),
};

// Provider Companies API
export const providerCompaniesAPI = {
  getAll: () => api.get('/provider-companies'),
  create: (company) => api.post('/provider-companies', company),
};

// Users API
export const usersAPI = {
  getAll: (providerCompanyId) => api.get('/users', { params: { provider_company_id: providerCompanyId } }),
  toggleActive: (userId) => api.put(`/users/${userId}/toggle-active`),
};

// Activity Log API
export const activityLogAPI = {
  getAll: (requestId, limit) => api.get('/activity-log', { params: { request_id: requestId, limit } }),
};

// Credential Requests API
export const credentialRequestsAPI = {
  getAll: (status) => api.get('/credential-requests', { params: { status } }),
  getById: (id) => api.get(`/credential-requests/${id}`),
  create: (data) => api.post('/credential-requests', data),
  uploadRepresentatives: (requestId, representatives) => {
    const formData = new FormData();
    formData.append('representatives', JSON.stringify(representatives));
    return api.post(`/credential-requests/${requestId}/upload-representatives`, formData);
  },
  uploadPhoto: (requestId, repIndex, file) => {
    const formData = new FormData();
    formData.append('rep_index', repIndex);
    formData.append('photo', file);
    return api.post(`/credential-requests/${requestId}/upload-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  confirmPayment: (requestId) => api.post(`/credential-requests/${requestId}/confirm-payment`),
  approve: (requestId) => api.post(`/credential-requests/${requestId}/approve`),
  reject: (requestId, comments) => api.post(`/credential-requests/${requestId}/reject`, null, { params: { comments } }),
};

// ============ LOYALTY SYSTEM APIs ============

// Badges API
export const badgesAPI = {
  getAll: (category, activeOnly = true) => api.get('/loyalty/badges', { params: { category, active_only: activeOnly } }),
  getById: (id) => api.get(`/loyalty/badges/${id}`),
  create: (badge) => api.post('/loyalty/badges', badge),
  update: (id, badge) => api.put(`/loyalty/badges/${id}`, badge),
  delete: (id) => api.delete(`/loyalty/badges/${id}`),
  getCategories: () => api.get('/loyalty/badge-categories'),
  assign: (badgeId, providerIds, message) => api.post('/loyalty/badges/assign', {
    badge_id: badgeId,
    provider_company_ids: providerIds,
    message
  }),
  getAssignments: (providerId, badgeId) => api.get('/loyalty/badges/assignments', {
    params: { provider_company_id: providerId, badge_id: badgeId }
  }),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/loyalty/upload/badge-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Benefits API
export const benefitsAPI = {
  getAll: (category, activeOnly = true) => api.get('/loyalty/benefits', { params: { category, active_only: activeOnly } }),
  getById: (id) => api.get(`/loyalty/benefits/${id}`),
  create: (benefit) => api.post('/loyalty/benefits', benefit),
  update: (id, benefit) => api.put(`/loyalty/benefits/${id}`, benefit),
  delete: (id) => api.delete(`/loyalty/benefits/${id}`),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/loyalty/upload/benefit-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Redemptions API
export const redemptionsAPI = {
  getAll: (status, providerId) => api.get('/loyalty/redemptions', {
    params: { status_filter: status, provider_company_id: providerId }
  }),
  create: (benefitId) => api.post('/loyalty/redemptions', { benefit_id: benefitId }),
  updateStatus: (id, status, notes) => api.put(`/loyalty/redemptions/${id}/status`, {
    status,
    admin_notes: notes
  }),
};

// Communications API
export const communicationsAPI = {
  getAll: (status, includeDrafts = false) => api.get('/loyalty/communications', {
    params: { status_filter: status, include_drafts: includeDrafts }
  }),
  getById: (id) => api.get(`/loyalty/communications/${id}`),
  create: (comm) => api.post('/loyalty/communications', comm),
  update: (id, comm) => api.put(`/loyalty/communications/${id}`, comm),
  send: (id) => api.post(`/loyalty/communications/${id}/send`),
  delete: (id) => api.delete(`/loyalty/communications/${id}`),
  uploadMedia: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/loyalty/upload/communication-media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Ranking API
export const rankingAPI = {
  get: (periodType, startDate, endDate, limit = 50) => api.get('/loyalty/ranking', {
    params: { period_type: periodType, start_date: startDate, end_date: endDate, limit }
  }),
  getConfig: () => api.get('/loyalty/ranking/config'),
  updateConfig: (config) => api.put('/loyalty/ranking/config', config),
};

// Feature Toggles API
export const featureTogglesAPI = {
  getAll: () => api.get('/loyalty/features'),
  update: (featureName, isEnabled) => api.put('/loyalty/features', {
    feature_name: featureName,
    is_enabled: isEnabled
  }),
};

// Provider Loyalty Portal API
export const loyaltyPortalAPI = {
  getMyPoints: () => api.get('/loyalty/my-points'),
  getMyBadges: () => api.get('/loyalty/my-badges'),
  getMyRedemptions: (status) => api.get('/loyalty/my-redemptions', { params: { status_filter: status } }),
  getNewsfeed: (limit = 20) => api.get('/loyalty/newsfeed', { params: { limit } }),
};

// Request Modifications API
export const modificationsAPI = {
  getAll: (requestId) => api.get(`/requests/${requestId}/modifications`),
  create: (requestId, data) => api.post(`/requests/${requestId}/modify`, data),
  review: (requestId, modificationId, data) => 
    api.post(`/requests/${requestId}/modifications/${modificationId}/review`, data),
  getPreview: (requestId) => api.get(`/requests/${requestId}/modification-preview`),
  getPendingModifications: () => api.get('/requests/pending-modifications'),
};

// Notifications API (Facebook-style)
export const notificationsAPI = {
  getAll: (limit = 50, unreadOnly = false) => 
    api.get('/notifications', { params: { limit, unread_only: unreadOnly } }),
  getCount: () => api.get('/notifications/count'),
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.post('/notifications/mark-all-read'),
};